// Pure cell/column classification helpers: regexes plus a status
// vocabulary. No React, no DOM.

export type StatusVariant = "positive" | "warning" | "negative" | "terminal";

// The Polly system prompt (`agent_chat_service/constants.py`) lists
// these words verbatim. Update both, plus the Langfuse
// `chat/agent-system-message`, in the same change window; drift
// causes badge fallback in `inferStatusVariant` below.
export const STATUS_KEYWORDS = {
    positive: [
        "quoted",
        "bound",
        "active",
        "approved",
        "accepted",
        "paid",
        "won",
    ],
    warning: ["pending", "in review", "open", "submitted", "awaiting"],
    negative: ["declined", "denied", "expired", "lost", "withdrawn"],
    terminal: ["closed", "resolved", "cancelled"],
} as const satisfies Record<StatusVariant, readonly string[]>;

export function isStatusVariant(s: string): s is StatusVariant {
    return Object.prototype.hasOwnProperty.call(STATUS_KEYWORDS, s);
}

// Currency: `$` then digits, optional comma-grouped run, optional 1-2
// fractional digits. 3+ fractional digits are rejected; USD never
// carries that precision in our corpus, and the extra digit is almost
// always a number mistyped as currency.
const CURRENCY_BODY = "\\$\\d[\\d,]*(?:\\.\\d{1,2})?";
const CURRENCY_RE = new RegExp(`^${CURRENCY_BODY}$`);

// Capturing-group form of `CURRENCY_BODY` (no anchors); `String.split`
// against it returns alternating prose / currency segments for
// `highlightCurrencyTokens`.
export const CURRENCY_TOKEN_RE = new RegExp(`(${CURRENCY_BODY})`);

const DATE_RE = /^(?:\d{2}\/\d{2}\/\d{4}|\d{4}-\d{2}-\d{2})$/;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const EMDASH_SENTINELS = new Set(["", "—", "-", "n/a", "null"]);

const STATUS_HEADER_RE = /\b(?:status|state|outcome|result)\b/i;

// Boolean column trigger: header must hint at a yes/no semantic AND at
// least 2 cells parse as bool. Both are required; free-form notes
// columns occasionally contain "Yes"/"No" cells without being boolean.
const BOOL_HEADER_RE =
    /required|eligible|present|enabled|approved|active|carve.?back/i;
const BOOL_TRUE_RE = /^(?:yes|true|y)$/i;
const BOOL_FALSE_RE = /^(?:no|false|n)$/i;
const BOOL_MIN_CELL_MATCHES = 2;

export function parseBoolText(text: string): boolean | null {
    const trimmed = text.trim();
    if (BOOL_TRUE_RE.test(trimmed)) return true;
    if (BOOL_FALSE_RE.test(trimmed)) return false;
    return null;
}

export function isBoolText(text: string): boolean {
    return parseBoolText(text) !== null;
}

export function detectCurrency(text: string): boolean {
    return CURRENCY_RE.test(text.trim());
}

// Returns `null` for malformed input; callers normalize that to
// `undefined` so `sortUndefined: "last"` pushes the row down in both
// sort directions.
export function parseCurrencyValue(text: string): number | null {
    const trimmed = text.trim();
    if (!CURRENCY_RE.test(trimmed)) return null;
    const numeric = trimmed.replace(/[$,]/g, "");
    const value = Number(numeric);
    return Number.isFinite(value) ? value : null;
}

export function detectDate(text: string): boolean {
    return DATE_RE.test(text.trim());
}

// Returns UTC-midnight epoch ms (or `null`). Pinning to UTC keeps the
// sort deterministic regardless of the runner's local timezone.
export function parseDateValue(text: string): number | null {
    const trimmed = text.trim();
    if (!DATE_RE.test(trimmed)) return null;
    const iso = ISO_DATE_RE.test(trimmed)
        ? trimmed
        : (() => {
              const [mm, dd, yyyy] = trimmed.split("/");
              return `${yyyy}-${mm}-${dd}`;
          })();
    const ts = Date.parse(`${iso}T00:00:00Z`);
    return Number.isFinite(ts) ? ts : null;
}

export function detectEmDash(text: string): boolean {
    return EMDASH_SENTINELS.has(text.trim().toLowerCase());
}

export function detectBooleanColumn(
    headerText: string,
    cellSamples: string[]
): boolean {
    if (!BOOL_HEADER_RE.test(headerText)) return false;
    let matches = 0;
    for (const cell of cellSamples) {
        if (isBoolText(cell)) matches++;
        if (matches >= BOOL_MIN_CELL_MATCHES) return true;
    }
    return false;
}

export function detectRowCounterColumn(headerText: string): boolean {
    return headerText.trim() === "#";
}

export function isStatusColumn(headerText: string): boolean {
    return STATUS_HEADER_RE.test(headerText.trim());
}

// Safety net for cells the LLM didn't tag with `<span data-status>`.
// Returns `null` for unknown words so the renderer falls through to
// plain text rather than inventing a colour.
export function inferStatusVariant(text: string): StatusVariant | null {
    const needle = text.trim().toLowerCase();
    if (!needle) return null;
    for (const variant of Object.keys(STATUS_KEYWORDS) as StatusVariant[]) {
        if ((STATUS_KEYWORDS[variant] as readonly string[]).includes(needle)) {
            return variant;
        }
    }
    return null;
}
